'use client';

import { AutocompleteDesktop } from './autocomplete-desktop';
import { AutocompleteMobile } from './autocomplete-mobile';
import type {
  AutocompleteProps,
  ResolvedAutocompleteProps,
} from './autocomplete-types';
import { useAutocomplete } from './use-autocomplete';

// Types live in `autocomplete-types.ts`; behavior in `use-autocomplete.ts`; the
// option list, mobile sheet, and desktop combobox in their sibling
// `autocomplete-*.tsx` files (conformance, code-size guard). Re-exported here so
// consumers keep importing from `@kit/ui/autocomplete` unchanged.
export type {
  AutocompleteFieldProps,
  AutocompleteOption,
  AutocompleteProps,
} from './autocomplete-types';

/**
 * Accessible autocomplete/combobox component with mobile-friendly design.
 *
 * Features:
 * - Full keyboard navigation (arrow keys, enter, escape)
 * - ARIA compliant with proper roles and attributes
 * - Mobile-optimized with bottom sheet on small screens
 * - Debounced input for async search
 * - Multi-select support
 * - Custom option rendering
 * - Loading and empty states
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Autocomplete
 *   value={selected}
 *   onChange={setSelected}
 *   options={cities}
 *   placeholder="Search cities..."
 *   label="City"
 * />
 *
 * // Async search
 * <Autocomplete
 *   value={selected}
 *   onChange={setSelected}
 *   options={results}
 *   loading={isLoading}
 *   onInputChange={(term) => search(term)}
 *   placeholder="Search..."
 * />
 * ```
 */
export function Autocomplete(rawProps: AutocompleteProps) {
  // Resolve defaults once so the hook and both view components share identical,
  // default-applied values.
  const props: ResolvedAutocompleteProps = {
    ...rawProps,
    placeholder: rawProps.placeholder ?? 'Search...',
    required: rawProps.required ?? false,
    disabled: rawProps.disabled ?? false,
    loading: rawProps.loading ?? false,
    multiple: rawProps.multiple ?? false,
    creatable: rawProps.creatable ?? false,
    emptyMessage: rawProps.emptyMessage ?? 'No results found',
    loadingMessage: rawProps.loadingMessage ?? 'Loading...',
    minChars: rawProps.minChars ?? 0,
    debounceMs: rawProps.debounceMs ?? 300,
    serverFiltered: rawProps.serverFiltered ?? false,
    clearOnSelect: rawProps.clearOnSelect ?? false,
    autoFocus: rawProps.autoFocus ?? false,
    autoSelectOnBlur: rawProps.autoSelectOnBlur ?? false,
    selectOnFocus: rawProps.selectOnFocus ?? true,
  };

  const state = useAutocomplete(props);

  if (state.isMobile) {
    return <AutocompleteMobile props={props} state={state} />;
  }

  return <AutocompleteDesktop props={props} state={state} />;
}

Autocomplete.displayName = 'Autocomplete';
